"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import Image from "next/image";
import { useRef } from "react";
import { A11y, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { usePathname } from "next/navigation";
import { IconBox } from "@/components/icon-box";
import Bg_Dots from "~/public/assets/backgrounds/bg-dots-2.png";
import type { ServicePageContent } from "../../../types/types";
import { useServiceCardsAnimation } from "./use-service-cards-animation";

type CapabilitiesServicesSectionProps = {
	service: Pick<ServicePageContent, "capabilities">;
};

type CapabilityCardProps = {
	item: NonNullable<ServicePageContent["capabilities"]>["items"][number];
	index: number;
	isSolutionsPage: boolean;
};

function CapabilityCard({ item, isSolutionsPage }: CapabilityCardProps) {
	return (
		<div
			data-service-card
			className={`h-full p-8 xl:p-12 rounded-2xl 
			${isSolutionsPage ? "bg-gray-900" : "border border-gray-300"}`}
		>
			{isSolutionsPage && item.icon && (
				<IconBox
					data-service-card-media
					bordered
					icon={item.icon}
					className="mb-9"
				/>
			)}
			<h3 className="text-lg md:text-xl font-medium whitespace-pre-line">
				{item.title}
			</h3>
			<p className="text-sm md:text-base mt-6 text-white/50">
				{item.description}
			</p>
		</div>
	);
}

export default function CapabilitiesServicesSection({
	service,
}: CapabilitiesServicesSectionProps) {
	const capabilities = service.capabilities;
	const sectionRef = useRef<HTMLElement | null>(null);
	const pathname = usePathname();

	const isSolutionsPage = pathname.includes("modernizacao-de-sistemas-legados");

	useServiceCardsAnimation(sectionRef, {
		preset: "drift",
	});

	if (!capabilities) return null;

	return (
		<section
			ref={sectionRef}
			className="relative border-y border-gray-300 bg-gray-1100 text-white"
		>
			<Image
				src={Bg_Dots}
				alt="Imagem de fundo de pontos para decoração"
				aria-hidden="true"
				className="absolute left-0 bottom-0 w-full h-auto max-w-[8%] 3xl:max-w-[9.8%] hidden lg:block"
			/>
			<Image
				src={Bg_Dots}
				alt="Imagem de fundo de pontos para decoração"
				aria-hidden="true"
				className="absolute right-0 bottom-0 w-full h-auto max-w-[8%] 3xl:max-w-[9.8%] hidden lg:block"
			/>

			<div className="md:container-padding">
				<div className="container relative md:border-x md:border-gray-300">
					<span className="hidden md:inline-flex absolute -top-1.75 -left-1.75 h-3 w-3 border border-gray-300 bg-gray-1000" />
					<span className="hidden md:inline-flex absolute -top-1.75 -right-1.75 h-3 w-3 border border-gray-300 bg-gray-1000" />

					<Image
						src={capabilities.featureImage}
						alt=""
						aria-hidden="true"
						className="w-full absolute z-10 top-0 right-0 max-w-105 max-md:opacity-70"
						width={2000}
						height={2000}
					/>

					<div className="py-18 px-6 lg:px-18 3xl:px-24! md:pb-32 relative z-20">
						<div className="max-w-160 md:min-w-90 max-md:relative max-md:z-20">
							<p className="tag">{capabilities.tag}</p>
							<h2 className="title">{capabilities.title}</h2>
							<p className="description mt-6 whitespace-pre-line">
								{capabilities.description}
							</p>
						</div>

						<div
							data-service-cards-animation-trigger
							className="mt-10 md:hidden"
						>
							<Swiper
								a11y={{ enabled: true }}
								className="overflow-visible!"
								modules={[Navigation, A11y]}
								navigation={{
									prevEl: ".capabilities-prev",
									nextEl: ".capabilities-next",
								}}
								slidesPerView="auto"
								spaceBetween={20}
							>
								{capabilities.items.map((item, index) => (
									<SwiperSlide
										className="h-auto! max-w-[92%] sm:max-w-lg pb-2"
										key={item.title}
									>
										<CapabilityCard
											item={item}
											index={index}
											isSolutionsPage={isSolutionsPage}
										/>
									</SwiperSlide>
								))}
							</Swiper>

							<div className="mt-5 flex items-center gap-5">
								<button
									aria-label="Card anterior"
									className="capabilities-prev cursor-pointer text-white transition-all duration-300 ease-in-out"
									type="button"
								>
									<ArrowLeft
										className="size-9 text-primary-300"
										strokeWidth={1}
									/>
								</button>
								<button
									aria-label="Próximo card"
									className="capabilities-next cursor-pointer text-white transition-all duration-300 ease-in-out"
									type="button"
								>
									<ArrowRight
										className="size-9 text-primary-300"
										strokeWidth={1}
									/>
								</button>
							</div>
						</div>

						<div
							data-service-cards-animation-trigger
							className="mt-16 hidden md:grid md:grid-cols-2 xl:grid-cols-3 relative z-20 gap-4"
						>
							{capabilities.items.map((item, index) => (
								<CapabilityCard
									key={item.title}
									item={item}
									index={index}
									isSolutionsPage={isSolutionsPage}
								/>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
